import isEmpty from "lodash/isEmpty";

import * as api from "../api/adapters/json_api";
import {poItemsToHeader} from "../converter/show";

const type = "api_resources/purchase_orders";
const typeItems = "purchase_orders";

// FIN-1283: the list is paged on the server. The browser used to break ties by the
// creation time, newest first; that stays, and the id — unique — comes last, so that
// LIMIT/OFFSET never repeats or loses a row between pages (FIN-1279).
const TIE_BREAK = "-created_at,-id";

// FIN-1281: "Imported first" puts the orders with a waiting imported invoice ahead of the
// user's own sort; it is a sort key for the server, not a filter, so it is not sent as one.
const sortOptions = (sort, filter = {}) => {
  const primary = !sort || isEmpty(sort.field)
    ? "-order_date"
    : `${sort.direction === "asc" ? "" : "-"}${sort.field}`;
  const first = filter && filter.imports_first ? "-pending_import," : "";
  return `${first}${primary},${TIE_BREAK}`;
};

const listFilter = ({ imports_first, ...rest } = {}) => rest;

const searchOptions = (search) => (isEmpty(search) ? null : { search });

// The list's companies and period live in the settings, not in the list's own filters.
// Callers without settings (the company documents popup) get no such filters.
const settingsFilter = (settings) =>
  settings && settings.global
    ? {
        vessel_company_id: settings.global.poCompanies,
        period_filter: {
          budgetYear: settings.global.budgetYear,
          periodType: settings.purchaseOrders && settings.purchaseOrders.periodType,
        },
      }
    : {};

const noCompanySelected = (settings) =>
  settings && settings.global && isEmpty(settings.global.poCompanies);

const emptyPage = () =>
  Promise.resolve({ data: { data: [], included: [], meta: { page_count: 0, record_count: 0 } } });

const indexFields = [
  "created_from_import_id", // FIN-1308
  "presentation",
  "description",
  "order_date",
  "number",
  "budget_year",
  "currency",
  "company_id",
  "vessel_id",
  "status",
  "date_paid",
  "sum",
  "sum_usd",
  "created_at",
  "updated_at",
  "created_by_id",
  "port_id",
  "agent_id",
  "eta",
  "etd",
  "supplied",
  "message_number",
  "delivery_description",
  "approving_status",
  "user_pending_approve",
  "delivery_string",
  // FIN-1283: what the list row shows, resolved on the server
  "vessel_name",
  "vessel_imo",
  "company_name",
  "company_short_name",
  "company_email",
  "company_purchasing_email",
  "port_name",
  "port_country_name",
  "total_weight",
  "awb_number",
  "warehouse_location_id",
  "warehouse_location_name",
  "warehouse_location_country_name",
  "logistics_company",
  "invoicing",
  "email_sent",
  "last_updated_by",
  "last_updated_at",
  "related_documents",
  "logistic_status",
  // FIN-1281: an imported supplier invoice waiting on the order, for the Inv indicator
  "pending_import",
  "pending_import_id",
].join(",");

const showFields = [
  "created_from_import", // FIN-1308
  "presentation",
  "description",
  "order_date",
  "number",
  "budget_year",
  "currency",
  "company_id",
  "vessel_id",
  "status",
  "date_paid",
  "sum",
  "sum_usd",
  "created_at",
  "updated_at",
  "created_by_id",
  "port_id",
  "agent_id",
  "eta",
  "etd",
  "supplied",
  "message_number",
  "delivery_description",
  "approving_status",
  "user_pending_approve",
  "delivery_string",
  "items",
  "related_documents",
  "self_present",
  "country_id",
  "custom_rate",
  "custom_rate_currency",
  "logistic_status",
  "date_of_readiness",
  "weight",
  "dimensions",
  "estimated_shipping_cost",
  "awb_number",
  "warehouse_location",
  "logistics_company",
  "warehouse_location_id",
  "logistics_company_id",
  "on_schedule",
  "acknowledged_date",
  "parcels",
  // FIN-1266: which template and period the order was generated for, and whether
  // it can become a template.
  "recurring",
  "templatable",
].join(",");

// FIN-1261: the PDF of the orders the list shows under the Port filter — the list's own filters, search and sort.
export const portPdfQuery = (sort, search, filter, settings) => ({
  sort: sortOptions(sort, filter),
  filter: { ...listFilter(filter), ...searchOptions(search), ...settingsFilter(settings) },
});

export default {
  /**
   * FIN-1243: the Purchase Orders block of the Dashboard's Purchasing section,
   * GET /api_resources/purchase_orders/dashboard_purchasing. No vessels means every vessel
   * the user may see; 'all' for importance or type means no filter and is not sent.
   * @returns {Promise<Object>} the block, as the backend's `data`
   */
  dashboardPurchasing: ({ vesselIds, importance, type: rlType } = {}) => api
    .read({ type, id: "dashboard_purchasing" }, {
      query: {
        vessel_ids: vesselIds,
        importance: importance === "all" ? undefined : importance,
        type: rlType === "all" ? undefined : rlType,
      },
    })
    .then((response) => response.data.data),

  totals: (options) => api.read({type, id: "totals"}, {query: {...options}}),

  /**
   * FIN-1260: the ports the Port filter offers — of the orders of the list's companies and period, with the country
   * and how many orders. A port is a city of UN/LOCODE: the list of them all is no choice.
   * @returns {Promise<Array<{id, name, country, count}>>}
   */
  ports: ({ companies, budgetYear, periodType }) => api
    .read({ type, id: "ports" }, {
      query: { filter: { vessel_company_id: companies, period_filter: { budgetYear, periodType } } },
    })
    .then((response) => response.data.data),

  start_approve: ({id, rounds}) =>
    api.update({type, id, action: "start_approve"}, {}, {rounds}),

  show: (id) => {
    const result = api
    .read(
      {type, id},
      {
        query: {
          include: "items",
          fields: {[typeItems]: showFields},
        },
      }
    )
    .then(poItemsToHeader);
    return result;
  },
  index: (pagination, sort, search, filter, settings, options = {fields: null}) =>
    noCompanySelected(settings)
      ? emptyPage()
      : api.read(
        {type},
        {
          query: {
            sort: sortOptions(sort, filter),
            page: {size: pagination.itemsPerPage, number: pagination.activePage},
            filter: {...listFilter(filter), ...searchOptions(search), ...settingsFilter(settings)},
            fields: options.fields || {[typeItems]: indexFields},
          },
        }
      ),

  create: (attributes) => api.create({type}, {}, attributes),

  update: (id, attributes) => api.update({id, type}, {}, attributes),

  approve_process: (id) => api.read({type, id, action: "approve_process"}),

  approve: (id) => api.read({type, id, action: "approve"}),

  reject: (id) => api.read({type, id, action: "reject"}),

  delete: (id) => api.destroy({id, type}),

  poMailContent: (id) => api.read({type, id, action: "order_mailer_content"}),

  poMailSend: (id, options) =>
    api.update({type, id, action: "order_mailer_send_mail"}, {}, options, true),

  versions: (id) => api.read({type, id, action: "versions"}),

  compare: (id, leftId, rightId) => api.read({type, id, action: "compare"}, {
    query: {
      left_id: leftId,
      right_id: rightId
    }
  }),
};
